/**
 * 通道运行模式标签（真实 / 本地解析 / 沙箱 / 人工 / 未就绪）
 *
 * 【为什么必须有这个组件】
 * 从前的校验报告只显示「校验成功」四个字，**看不出这一步用的是真实通道
 * 还是沙箱**。于是一张真实发票的四项校验全部显示绿色，实际全是沙箱伪造
 * 的数据，还据此报出了一条中风险「票面 194308.42 vs 税务 54000.00」。
 *
 * 这个标签的职责就是让"数据从哪来"**无法被忽略**：沙箱数据永远带着
 * 蓝色「沙箱模拟」标，未接通的通道永远带着红色「通道未启用」标，
 * 打印出来的报告也一样（样式必须落在打印区域内）。
 *
 * 【mode 为 undefined 时显示「未记录」】
 * 历史报告的 `orchestration_log` 里没有 mode 字段。**绝不能兜底成"真实"**——
 * 那正是这类问题的根源：未知值静默落到"看起来正常"的分支。
 */

import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloudServerOutlined,
  DisconnectOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  ProviderMode,
  PROVIDER_MODE_LABELS,
  PROVIDER_MODE_DESCRIPTIONS,
} from '@ilc/shared';

const MODE_COLORS: Record<ProviderMode, string> = {
  [ProviderMode.REAL]: 'green',
  // 本地解析也是真实数据，但不产生外部调用，用青色与 REAL 区分
  [ProviderMode.LOCAL]: 'cyan',
  [ProviderMode.SANDBOX]: 'blue',
  [ProviderMode.MANUAL]: 'orange',
  [ProviderMode.UNAVAILABLE]: 'red',
};

const MODE_ICONS: Record<ProviderMode, React.ReactNode> = {
  [ProviderMode.REAL]: <CloudServerOutlined />,
  [ProviderMode.LOCAL]: <FileSearchOutlined />,
  [ProviderMode.SANDBOX]: <ExperimentOutlined />,
  [ProviderMode.MANUAL]: <UserSwitchOutlined />,
  [ProviderMode.UNAVAILABLE]: <DisconnectOutlined />,
};

/** 沙箱与未就绪都不产生真实结论，需在视觉上强提示 */
const WARNING_MODES: ReadonlySet<ProviderMode> = new Set([
  ProviderMode.SANDBOX,
  ProviderMode.UNAVAILABLE,
]);

export interface ProviderModeTagProps {
  mode: ProviderMode | null | undefined;
  /** 具体实现名，如 aliyun-verify-vat-invoice / pdf-text / sandbox */
  impl?: string | null;
  /** 是否把实现名拼在标签后面 */
  showImpl?: boolean;
  /** 校验项名称，用于 tooltip 文案 */
  itemName?: string;
}

export function ProviderModeTag({
  mode,
  impl,
  showImpl = true,
  itemName,
}: ProviderModeTagProps): React.ReactElement {
  // 历史数据没有 mode —— 如实说"未记录"，不要猜
  if (!mode) {
    return (
      <Tooltip title="这份校验报告生成于通道信息落库之前，无法判断当时用的是真实通道还是沙箱">
        <Tag style={{ marginInlineEnd: 0 }}>通道未记录</Tag>
      </Tooltip>
    );
  }

  const label = PROVIDER_MODE_LABELS[mode] ?? mode;
  const description = PROVIDER_MODE_DESCRIPTIONS[mode] ?? '';
  const text = showImpl && impl ? `${label} · ${impl}` : label;

  const tag = (
    <Tag
      color={MODE_COLORS[mode] ?? 'default'}
      icon={MODE_ICONS[mode]}
      style={{ marginInlineEnd: 0 }}
    >
      {text}
    </Tag>
  );

  const prefix = itemName ? `【${itemName}】` : '';
  return <Tooltip title={`${prefix}${description}`}>{tag}</Tooltip>;
}

/** 判断某一步是否"未走真实外部通道"，用于页级提示与报告分章 */
export function isUntrustedMode(mode: ProviderMode | null | undefined): boolean {
  return mode !== undefined && mode !== null && WARNING_MODES.has(mode);
}

export default ProviderModeTag;
